// XrHit.js
import React, { useRef, useState, useEffect, useCallback } from "react";
import { useXR, XRHitTest } from "@react-three/xr";
import * as THREE from "three";
import { useAnimations, Bounds } from "@react-three/drei";
import { useGateAnimation } from "./components/hooks/useGateAnimation";
import { useGateModel } from "./components/hooks/useGateModel";

const XrHit = ({
  setModelParams,
  modelParams,
  onModelLoaded,
  gateControlRef,
  onGateAnimationFinished,
  onModelPlacedChange,
}) => {
  const reticleRef = useRef();
  const modelRef = useRef();
  const [model, setModel] = useState(null); // { position, rotation, scale }

  const session = useXR((state) => state.session);
  const isPresenting = !!session;

  // Инициализация модели
  const { scene, animations, initialized } = useGateModel({
    onModelLoaded,
    setModelParams,
    isGateOpened: modelParams?.isGateOpened,
  });

  // === Анимация ворот ===
  const { actions } = useAnimations(animations, modelRef);

  const handleGateToggle = useCallback(
    (isOpen) => {
      setModelParams?.((prev) => ({ ...prev, isGateOpened: isOpen }));
    },
    [setModelParams],
  );

  const { toggleGate } = useGateAnimation(
    actions,
    handleGateToggle,
    onGateAnimationFinished,
  );

  // Пробрасываем управление воротами наружу (аналог modelControllerRef в Viewer3D)
  useEffect(() => {
    if (!gateControlRef) return;
    gateControlRef.current = { toggleGate };
    return () => {
      if (gateControlRef.current?.toggleGate === toggleGate) {
        gateControlRef.current = null;
      }
    };
  }, [gateControlRef, toggleGate]);

  // Сообщаем наверх, поставлена ли модель на плоскость
  useEffect(() => {
    onModelPlacedChange?.(!!model);
  }, [model, onModelPlacedChange]);

  const matrixHelper = new THREE.Matrix4();

  // Камеру в preview теперь ставит <Bounds> (раньше useThree сбрасывал
  // её позицию на каждом рендере и мешал вращать модель)

  // Обновление видимости активных ворот
  useEffect(() => {
    if (!scene || !modelParams?.gates || !modelParams.activeGate) return;
    Object.entries(modelParams.gates).forEach(([name]) => {
      const obj = scene.getObjectByName(name);
      if (obj) obj.visible = name === modelParams.activeGate;
    });
  }, [modelParams?.activeGate, scene]);

  // === Управление моделью (перемещение, поворот и т.д.) ===
  const moveModel = (direction) => {
    if (!model) return;
    const move = 0.1;
    const pos = model.position.clone();
    switch (direction) {
      case "forward":
        pos.z -= move;
        break;
      case "backward":
        pos.z += move;
        break;
      case "left":
        pos.x -= move;
        break;
      case "right":
        pos.x += move;
        break;
      case "up":
        pos.y += move;
        break;
      case "down":
        pos.y -= move;
        break;
    }
    const newModel = { ...model, position: pos };
    setModel(newModel);
    if (modelRef.current) modelRef.current.position.copy(pos);
  };

  const rotateModel = (dir) => {
    if (!model) return;
    const angle = Math.PI / 24;
    const rotY = (model.rotation?.y || 0) + (dir === "cw" ? angle : -angle);
    const newModel = { ...model, rotation: { y: rotY } };
    setModel(newModel);
    if (modelRef.current) modelRef.current.rotation.y = rotY;
  };

  const scaleModel = (dir) => {
    if (!model) return;
    let scale = model.scale || 1;
    scale = dir === "up" ? scale * 1.2 : Math.max(scale / 1.2, 0.1);
    const newModel = { ...model, scale };
    setModel(newModel);
    if (modelRef.current) modelRef.current.scale.setScalar(scale);
  };

  const removeModel = () => setModel(null);

  // === Обработчики DOM-кнопок ===
  // Кнопка ворот сюда больше не входит — она управляется через gateControlRef
  useEffect(() => {
    if (!isPresenting) return;

    const handlers = {
      "move-forward": () => moveModel("forward"),
      "move-backward": () => moveModel("backward"),
      "move-left": () => moveModel("left"),
      "move-right": () => moveModel("right"),
      "move-up": () => moveModel("up"),
      "move-down": () => moveModel("down"),
      "rotate-cw": () => rotateModel("cw"),
      "rotate-ccw": () => rotateModel("ccw"),
      "scale-up": () => scaleModel("up"),
      "scale-down": () => scaleModel("down"),
      "remove-cube": removeModel,
    };

    const buttons = Object.keys(handlers).map((id) => {
      const btn = document.getElementById(id);
      if (btn) btn.addEventListener("click", handlers[id]);
      return { btn, handler: handlers[id] };
    });

    return () => {
      buttons.forEach(({ btn, handler }) => {
        if (btn) btn.removeEventListener("click", handler);
      });
    };
  }, [isPresenting, model]);

  // === Hit-test и reticle ===
  const handleHitTestResults = (results, getWorldMatrix) => {
    if (!results?.[0] || !reticleRef.current || model) return;
    getWorldMatrix(matrixHelper, results[0]);
    const pos = new THREE.Vector3().setFromMatrixPosition(matrixHelper);
    reticleRef.current.position.copy(pos);
    reticleRef.current.rotation.set(-Math.PI / 2, 0, 0);
  };

  const handleReticleClick = (e) => {
    e.stopPropagation();
    if (reticleRef.current && !model) {
      setModel({
        position: reticleRef.current.position.clone(),
        rotation: { y: 0 },
        scale: 1,
      });
    }
  };

  // === Обработка контроллера (select) ===
  useEffect(() => {
    if (!session) return;
    const onSelect = (e) => {
      if (e.inputSource.handedness === "left" && reticleRef.current && !model) {
        setModel({
          position: reticleRef.current.position.clone(),
          rotation: { y: 0 },
          scale: 1,
        });
      }
    };
    session.addEventListener("select", onSelect);
    return () => session.removeEventListener("select", onSelect);
  }, [session, model]);

  return (
    <>
      {/* === Preview на стартовой странице: модель с вращением камеры === */}
      {!isPresenting && initialized && scene && (
        <Bounds fit clip observe margin={1.2}>
          <primitive
            object={scene}
            ref={modelRef}
            position={[0, 0, 0]}
            rotation={[0, 0, 0]}
            scale={1}
          />
        </Bounds>
      )}

      {isPresenting && (
        <>
          <XRHitTest onResults={handleHitTestResults} space="viewer" />
          {!model && (
            <mesh
              ref={reticleRef}
              rotation={[-Math.PI / 2, 0, 0]}
              onClick={handleReticleClick}>
              <ringGeometry args={[0.05, 0.1, 32]} />
              <meshStandardMaterial
                color="white"
                transparent
                opacity={0.8}
                side={THREE.DoubleSide}
              />
            </mesh>
          )}

          {model && initialized && scene && (
            <primitive
              object={scene}
              ref={modelRef}
              position={model.position}
              rotation={[0, model.rotation?.y || 0, 0]}
              scale={model.scale || 1}
            />
          )}
        </>
      )}
    </>
  );
};

export default XrHit;
